import { z } from 'zod';
import { SelfCheckoutCodeType } from '../../../../../lib/interfaces/organisation';
import { PackageDisplayConfig } from '../../../../register/checkout/step2/config/package-mapper';
import { BillingFrequency } from '../../../../register/checkout/step2/config/packages-display.config';
import { PackageCategory } from '../../../../register/checkout/step2/enums/package-category.enum';

export const isInvoicePayment = (country: string | undefined, codeType?: string, codeValid?: boolean): boolean => {
  if (country !== 'DE') return true;
  return codeValid === true && codeType === SelfCheckoutCodeType.RECHNUNG;
};

export const createBasePaymentDataSchema = () => {
  return z.object({
    invoiceMail: z.union([z.literal(''), z.string().email('Bitte geben Sie eine E-Mail Adresse ein')]),
    bic: z.string().optional(),
    iban: z.string().optional(),
    newCustomerDate: z.string().min(1, {
      message: 'Startdatum ist erforderlich'
    })
  });
};

export const createCheckoutPaymentDataSchema = (packages?: Record<PackageCategory, PackageDisplayConfig>) => {
  const packageIds: string[] = packages
    ? [
        ...Object.values(packages).map((pkg) => pkg[BillingFrequency.MONTHLY].id),
        ...Object.values(packages).map((pkg) => pkg[BillingFrequency.QUARTERLY].id),
        ...Object.values(packages).map((pkg) => pkg[BillingFrequency.HALFYEARLY].id)
      ]
    : [];

  return z.object({
    code: z.string().optional(),
    codeValid: z.boolean().optional(),
    codeType: z.string().optional(),
    package: z.string().refine((data) => packageIds.includes(data), {
      message: 'Bitte wählen Sie ein gültiges Paket aus.'
    }),
    termsOfService: z.boolean().refine((data) => data, {
      message: 'Sie müssen die Nutzungsbedingungen akzeptieren'
    }),
    paymentConfirmation: z.boolean().optional(),
    minimumDuration: z.boolean().refine((data) => data, {
      message: 'Sie müssen die Bedingungen akzeptieren'
    })
  });
};

interface CreateDataSchemaWithIbanValidationOptions {
  validateIban?: (iban: string, bic: string) => Promise<{ iban: boolean; bic: boolean }>;
  country: string | undefined;
  /** Wenn gesetzt: überschreibt die Code/Country-Ableitung (z. B. Admin-Switch im RequestProcessor). */
  forceUseInvoice?: boolean;
}

export const createDataSchemaWithIbanValidation = <T extends BasePaymentDataSchemaType>(
  schema: z.ZodSchema<T>,
  options: CreateDataSchemaWithIbanValidationOptions
) => {
  const refinedSchema = schema.superRefine(async (data, ctx) => {
    // useInvoice ermitteln:
    // - Admin-Flow liefert forceUseInvoice (Switch-State)
    // - Customer-Flow leitet aus country + codeType/codeValid ab
    const dataAny = data as Record<string, unknown>;
    const codeType = dataAny.codeType as string | undefined;
    const codeValid = dataAny.codeValid as boolean | undefined;
    const useInvoice =
      options.forceUseInvoice !== undefined ? options.forceUseInvoice : isInvoicePayment(options.country, codeType, codeValid);

    if (useInvoice) return; // Rechnungs-Pfad → keine SEPA-Validierung

    // SEPA-Pfad
    if (!data.iban) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'IBAN ist erforderlich', path: ['iban'] });
    }
    if (!data.bic) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'BIC ist erforderlich', path: ['bic'] });
    }
    // paymentConfirmation existiert nur im Customer-Flow-Schema, daher defensiv prüfen
    if ('paymentConfirmation' in dataAny && dataAny.paymentConfirmation !== true) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Sie müssen die Zahlungsbestätigung bestätigen',
        path: ['paymentConfirmation']
      });
    }

    // IBAN-API-Validierung nur wenn beide Werte da
    if (options.validateIban && data.iban && data.bic) {
      const result = await options.validateIban(data.iban, data.bic);
      if (!result?.iban) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Ungültige IBAN', path: ['iban'] });
      }
      if (!result?.bic) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Ungültige BIC', path: ['bic'] });
      }
    }
  });

  return refinedSchema;
};

const basePaymentDataSchema = createBasePaymentDataSchema();
export type BasePaymentDataSchemaType = z.infer<typeof basePaymentDataSchema>;

const baseCheckoutPaymentDataSchema = createCheckoutPaymentDataSchema();
const checkoutPaymentDataSchema = z.intersection(basePaymentDataSchema, baseCheckoutPaymentDataSchema);

export type CheckoutPaymentDataSchemaType = z.infer<typeof checkoutPaymentDataSchema>;
